import { useEffect, useState, ReactNode } from 'react';
import { Modal, Backdrop, Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import { getPreference } from 'src/apis/preference';
import useNotification from 'src/hooks/useNotification';
import { Preference, PreferenceData } from 'src/types/preference';
import { All_Languages } from 'src/containers/sidebar/LanguageFilter';

interface ViewCampaignPolicyModalProps {
  open: boolean;
  preferenceId: string | null;
  closeModal: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    content: {
      width: '100%',
      maxHeight: 600,
      overflowY: 'auto',
      marginTop: 12,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    },
    section: {
      padding: 16,
      border: '1px solid #EAECF0',
      borderRadius: 8,
      background: '#F9FAFB',
    },
    sectionTitle: {
      marginBottom: 12,
      fontWeight: 600,
      fontSize: 16,
      color: '#101828',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      padding: '6px 0',
      borderBottom: '1px solid #EAECF0',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    rowLabel: {
      fontSize: 14,
      color: '#344054',
      flex: 1,
    },
    rowValue: {
      fontSize: 14,
      fontWeight: 600,
      color: '#101828',
    },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
    },
    chip: {
      padding: '4px 10px',
      borderRadius: 16,
      background: '#FFF',
      border: '1px solid #D0D5DD',
      fontSize: 13,
      color: '#344054',
    },
    empty: {
      fontSize: 14,
      color: '#98A2B3',
    },
    buttons: {
      paddingTop: 16,
      display: 'flex',
      justifyContent: 'flex-end',
    },
    loading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 200,
    },
    ruleBlock: {
      padding: 12,
      borderRadius: 8,
      background: '#FFF',
      border: '1px solid #EAECF0',
      marginBottom: 8,
      '&:last-child': {
        marginBottom: 0,
      },
    },
  })
);

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 720,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

const findLanguageName = (
  items: typeof All_Languages,
  code: string
): string | null => {
  for (const item of items) {
    if (item.id === code) {
      return item.name;
    }
    if (item.children) {
      const found = findLanguageName(item.children, code);
      if (found) return found;
    }
  }
  return null;
};

const ChipList = ({
  items,
  classes,
}: {
  items: string[];
  classes: ReturnType<typeof useStyles>;
}) => {
  if (!items.length) {
    return <Typography className={classes.empty}>None</Typography>;
  }
  return (
    <Box className={classes.chips}>
      {items.map((item) => (
        <span key={item} className={classes.chip}>
          {item}
        </span>
      ))}
    </Box>
  );
};

const PolicySection = ({
  title,
  children,
  classes,
}: {
  title: string;
  children: ReactNode;
  classes: ReturnType<typeof useStyles>;
}) => (
  <Box className={classes.section}>
    <Typography className={classes.sectionTitle}>{title}</Typography>
    {children}
  </Box>
);

export const ViewCampaignPolicyModal = ({
  open,
  preferenceId,
  closeModal,
}: ViewCampaignPolicyModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const { handleError } = useNotification();

  const [loading, setLoading] = useState(false);
  const [preference, setPreference] = useState<Preference | null>(null);

  useEffect(() => {
    if (!open || !preferenceId) {
      setPreference(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getPreference(preferenceId);
        if (!cancelled) {
          setPreference(data);
        }
      } catch (error) {
        if (!cancelled) {
          handleError(error, 'Failed to load campaign policy');
          closeModal();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, preferenceId, handleError, closeModal]);

  const data: PreferenceData | null = preference?.data ?? null;

  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby='view-campaign-policy-modal-title'
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 800,
      }}
    >
      <Box sx={{ ...modalStyle }}>
        <Box className={classes.header}>
          <Typography
            className={textClasses.lgBold}
            style={{ color: '#101828' }}
          >
            Campaign Policy
            {preference?.name ? ` — ${preference.name}` : ''}
          </Typography>
          <CancelIcon
            cursor='pointer'
            width={24}
            height={24}
            onClick={closeModal}
          />
        </Box>

        {loading || !data ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : (
          <Box className={classes.content}>
            <PolicySection title='Risk Preferences' classes={classes}>
              {Object.keys(data.preferences).length === 0 ? (
                <Typography className={classes.empty}>None</Typography>
              ) : (
                Object.entries(data.preferences).map(([key, value]) => (
                  <Box key={key} className={classes.row}>
                    <span className={classes.rowLabel}>{key}</span>
                    <span className={classes.rowValue}>{value}</span>
                  </Box>
                ))
              )}
            </PolicySection>

            <PolicySection title='Genre Exclusions' classes={classes}>
              <ChipList items={data.GENRE_EXCLUSIONS} classes={classes} />
            </PolicySection>

            <PolicySection
              title='Genre Exclusion Bypass Rules'
              classes={classes}
            >
              {data.GENRE_EXCLUSION_BYPASS_RULES.length === 0 ? (
                <Typography className={classes.empty}>None</Typography>
              ) : (
                data.GENRE_EXCLUSION_BYPASS_RULES.map((rule, idx) => (
                  <Box key={idx} className={classes.ruleBlock}>
                    <Typography style={{ fontSize: 14 }}>
                      <b>Genre:</b> {rule.genre || '—'}
                    </Typography>
                    <Typography style={{ fontSize: 14 }}>
                      <b>Risk category:</b> {rule.risk_category || '—'}
                    </Typography>
                    <Typography style={{ fontSize: 14 }}>
                      <b>Risk level:</b> {rule.risk_level || '—'}
                    </Typography>
                  </Box>
                ))
              )}
            </PolicySection>

            <PolicySection title='Show Exclusions' classes={classes}>
              <ChipList items={data.SHOW_EXCLUSIONS} classes={classes} />
            </PolicySection>

            <PolicySection
              title='Show Inclusions (Force Align)'
              classes={classes}
            >
              <ChipList
                items={data.SHOW_INCLUSIONS_FORCE_ALIGN0}
                classes={classes}
              />
            </PolicySection>

            <PolicySection title='IAB Top Exclusion Rules' classes={classes}>
              <ChipList
                items={data.IAB_TOP_EXCLUSION_RULES}
                classes={classes}
              />
            </PolicySection>

            <PolicySection title='Genre Risk Overrides' classes={classes}>
              {data.GENRE_RISK_OVERRIDES.length === 0 ? (
                <Typography className={classes.empty}>None</Typography>
              ) : (
                data.GENRE_RISK_OVERRIDES.map((override, idx) => (
                  <Box key={idx} className={classes.ruleBlock}>
                    <Typography style={{ fontSize: 14 }}>
                      <b>Pattern:</b> {override.pattern || '—'}
                    </Typography>
                    <Typography style={{ fontSize: 14 }}>
                      <b>Max risk:</b> {override.max_risk || '—'}
                    </Typography>
                    <Box style={{ marginTop: 8 }}>
                      <ChipList
                        items={override.columns || []}
                        classes={classes}
                      />
                    </Box>
                  </Box>
                ))
              )}
            </PolicySection>

            <PolicySection title='Language Policy' classes={classes}>
              <ChipList
                items={data.LANGUAGE_POLICY.map(
                  (code) => findLanguageName(All_Languages, code) || code
                )}
                classes={classes}
              />
            </PolicySection>
          </Box>
        )}

        <Box className={classes.buttons}>
          <Button
            title='Close'
            variant='outlined'
            height={46}
            onClick={closeModal}
          />
        </Box>
      </Box>
    </Modal>
  );
};
